<script setup lang="ts">
import {
  ref,
  nextTick,
  onMounted,
  onUnmounted,
  useTemplateRef,
  type PropType,
  type WatchStopHandle,
} from 'vue';
import { Menu } from '@ark-ui/vue/menu';
import { useElementHover, useWindowSize, watchThrottled } from '@vueuse/core';
import JumpTo from './JumpTo.vue';

const props = defineProps({
  menubarItems: {
    type: Array as PropType<Record<string, any>[]>,
    default: () => [],
  },

  routerMode: {
    type: Boolean,
    default: false,
  },
});

const menubarItemsEl = useTemplateRef<HTMLElement>('menubarItemsEl');
const overflowMenuEl = useTemplateRef<HTMLElement>('overflowMenuEl');
const overflowItems = ref<Record<string, any>[]>([]);
let isComponentActive = true;
let stopWatch: WatchStopHandle;

const { width: windowWith } = useWindowSize();
const isOverflowMenuHovered = useElementHover(overflowMenuEl, { delayEnter: 200, delayLeave: 200 });

const checkOverflowItems = () => {
  if (!isComponentActive || !menubarItemsEl.value) {
    return;
  }

  const listItems = menubarItemsEl.value.querySelectorAll<HTMLElement>('.js-menu-item');

  if (listItems.length === 0) {
    overflowItems.value = [];
    return;
  }

  listItems.forEach(item => {
    item.style.display = '';
  });

  let overflowIndex = -1;

  for (let i = 0; i < listItems.length; i++) {
    if (listItems[i].offsetTop > 0) {
      overflowIndex = i;
      break;
    }
  }

  if (overflowIndex < 0) {
    overflowItems.value = [];
    return;
  }

  listItems.forEach((item, index) => {
    item.style.display = index >= overflowIndex ? 'none' : '';
  });

  overflowItems.value = props.menubarItems.slice(overflowIndex);
};

const setMenuItemWidthVar = () => {
  if (menubarItemsEl.value) {
    const listItems = menubarItemsEl.value.querySelectorAll<HTMLElement>(':scope > li');

    listItems.forEach(item => {
      const width = item.offsetWidth;

      item.style.setProperty('--menu-item-width', `${width}px`);
    });
  }
};

onMounted(() => {
  stopWatch = watchThrottled(
    [windowWith, () => props.menubarItems],
    () => {
      nextTick(() => {
        checkOverflowItems();
        setMenuItemWidthVar();

        // 因为显示菜单三个点的时候，会占用宽度，所以需要再次计算
        nextTick(() => {
          checkOverflowItems();
          setMenuItemWidthVar();
        });
      });
    },
    { immediate: true, throttle: 50 },
  );
});

onUnmounted(() => {
  isComponentActive = false;
  stopWatch?.();
});
</script>

<template>
  <div class="h-full min-w-180 flex gap-x-20">
    <ul class="h-full flex flex-wrap justify-center gap-x-20 overflow-hidden" ref="menubarItemsEl">
      <template v-if="props.routerMode">
        <li class="h-64 flex items-center">
          <RouterLink to="/" class="whitespace-nowrap px-16 no-underline hover:text-color-primary-2 focus-visible:outline-unset">
            {{ $t('common.header.nav1') }}
          </RouterLink>
        </li>
        <li class="h-64 flex items-center">
          <RouterLink to="/explore" class="whitespace-nowrap px-16 no-underline hover:text-color-primary-2 focus-visible:outline-unset">
            {{ $t('common.header.nav2') }}
          </RouterLink>
        </li>
      </template>
      <template v-else>
        <li class="h-64 flex items-center">
          <a href="/" class="whitespace-nowrap px-16 no-underline hover:text-color-primary-2 focus-visible:outline-unset" target="_blank">
            {{ $t('common.header.nav1') }}
          </a>
        </li>
        <li class="h-64 flex items-center">
          <a href="/explore" class="whitespace-nowrap px-16 no-underline hover:text-color-primary-2 focus-visible:outline-unset" target="_blank">
            {{ $t('common.header.nav2') }}
          </a>
        </li>
      </template>

      <li
        class="group js-menu-item h-64 flex items-center"
        v-for="item in menubarItems"
        :key="item.barId"
      >
        <JumpTo :menubar-item="item" :router-mode="props.routerMode" class="whitespace-nowrap px-16 text-#292929 no-underline hover:text-color-primary-2" v-if="!item.children" />

        <template v-else>
          <div class="h-full flex cursor-pointer items-center">
            <div class="px-16 text-#292929 leading-24">{{ item.barName }}</div>
          </div>
          <div class="invisible absolute top-64 z-1 translate-x-[calc(-50%+var(--menu-item-width)/2)] rounded-6 bg-white px-8 py-10 opacity-0 shadow-[0_5px_15px_0_rgba(0,0,0,0.15)] transition-all duration-100 ease-linear group-hover:visible group-hover:opacity-100 focus-visible:outline-unset">
            <ul>
              <li v-for="group in item.children " :key="group.barId">
                <JumpTo :menubar-item="group" :router-mode="props.routerMode" class="block min-w-128 whitespace-nowrap rounded-4 text-center text-14 font-400 leading-32 hover:bg-#f7f7f7" />
              </li>
            </ul>
          </div>
        </template>
      </li>
    </ul>

    <div class="flex-shrink-0" ref="overflowMenuEl" v-show="overflowItems.length">
      <Menu.Root :open="isOverflowMenuHovered" lazy-mount close-on-select :positioning="{ placement: 'bottom', gutter: 0 }">
        <Menu.Trigger class="h-full w-16 bg-transparent text-14 text-#292929 leading-24 focus-visible:outline-unset">
          <span class="i-ion-ellipsis-horizontal block"></span>
        </Menu.Trigger>
        <Menu.Positioner>
          <Menu.Content as-child>
            <div class="rounded-6 bg-white px-8 py-10 shadow-[0_5px_15px_0_rgba(0,0,0,0.15)] focus-visible:outline-unset">
              <template v-for="item in overflowItems" :key="item.barId">
                <Menu.Item value="item.barName" as-child v-if="!item.children">
                  <JumpTo :menubar-item="item" :router-mode="props.routerMode" class="block min-w-128 whitespace-nowrap rounded-4 text-center text-14 font-400 leading-32 hover:bg-#f7f7f7" />
                </Menu.Item>

                <Menu.Root v-else close-on-select>
                  <Menu.TriggerItem as-child>
                    <div class="min-w-128 cursor-pointer whitespace-nowrap rounded-4 px-8 py-6 text-center text-14 font-400 leading-32 hover:bg-#f7f7f7">
                      {{ item.barName }}
                    </div>
                  </Menu.TriggerItem>
                  <Menu.Positioner>
                    <Menu.Content as-child>
                      <div class="rounded-6 bg-white px-8 py-10 shadow-[0_5px_15px_0_rgba(0,0,0,0.15)] focus-visible:outline-unset">
                        <Menu.Item :value="group.barName" as-child v-for="group in item.children" :key="group.barId">
                          <JumpTo :menubar-item="group" :router-mode="props.routerMode" class="block min-w-128 whitespace-nowrap rounded-4 text-center text-14 font-400 leading-32 hover:bg-#f7f7f7" />
                        </Menu.Item>
                      </div>
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              </template>
            </div>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    </div>
  </div>
</template>

<style lang="scss" scoped>
[data-scope=menu][data-part=content][data-state=open] {
  animation: fadeIn 0.1s linear;
}

[data-scope=menu][data-part=content][data-state=closed] {
  animation: fadeOut 0.1s linear;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}
</style>
